import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { logErrors } from 'utils';
import { API_ENDPOINTS } from 'utils/constants';
import { getAPI } from 'services/api/apiAsync';
import fetchInvitor from 'services/invitor/invitorAsync';
import {
  selectChildParticipants,
  selectInvitorLoaded,
} from 'services/invitor/invitorSelectors';
import { selectToken } from 'services/session/sessionSelectors';

export type ChildAlertsUnreadMap = Record<string, number>;

const unreadCache = new Map<string, number>();
let inflightBatch: Promise<ChildAlertsUnreadMap> | null = null;
let cacheVersion = 0;
const cacheListeners = new Set<() => void>();

function subscribeToChildAlertsUnreadCache(onStoreChange: () => void) {
  cacheListeners.add(onStoreChange);
  return () => {
    cacheListeners.delete(onStoreChange);
  };
}

function getChildAlertsUnreadCacheVersion() {
  return cacheVersion;
}

function bumpChildAlertsUnreadCache() {
  cacheVersion += 1;
  cacheListeners.forEach((listener) => listener());
}

/** Drop cached unread totals so the next fetch (or sidebar refresh) re-queries. */
export function invalidateChildAlertsUnreadCache(childId?: string) {
  if (childId) {
    unreadCache.delete(childId);
  } else {
    unreadCache.clear();
  }
  bumpChildAlertsUnreadCache();
}

/** Keep sidebar / plan-members badges in sync when a child alert is opened. */
export function setChildAlertsUnreadCache(childId: string, count: number) {
  unreadCache.set(childId, Math.max(0, count));
  bumpChildAlertsUnreadCache();
}

async function fetchUnreadForChild(
  dispatch: ReturnType<typeof useAppDispatch>,
  token: string | undefined,
  childId: string,
): Promise<number> {
  if (unreadCache.has(childId)) {
    return unreadCache.get(childId) ?? 0;
  }

  const response = await dispatch(
    getAPI({
      url: API_ENDPOINTS.alerts,
      params: {
        userIds: [childId],
        resolved: false,
        page: 0,
        pageSize: 1,
      },
      paramsSerializer: { indexes: null },
      headers: {
        'X-XSRF-TOKEN': token,
      },
    }),
  ).unwrap();

  const count = (response as { totalUnreadCount?: number })?.totalUnreadCount;
  const normalized = typeof count === 'number' ? count : 0;
  unreadCache.set(childId, normalized);
  return normalized;
}

function fetchChildAlertsUnreadCounts(
  dispatch: ReturnType<typeof useAppDispatch>,
  token: string | undefined,
  childIds: string[],
): Promise<ChildAlertsUnreadMap> {
  if (childIds.length === 0) {
    return Promise.resolve({});
  }

  if (inflightBatch) {
    return inflightBatch;
  }

  inflightBatch = Promise.all(
    childIds.map(async (childId) => {
      try {
        const count = await fetchUnreadForChild(dispatch, token, childId);
        return [childId, count] as const;
      } catch (err) {
        logErrors(err);
        unreadCache.set(childId, 0);
        return [childId, 0] as const;
      }
    }),
  )
    .then((entries) => Object.fromEntries(entries))
    .finally(() => {
      inflightBatch = null;
    });

  return inflightBatch;
}

function useChildAlertsUnreadCounts(enabled = true) {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const invitorLoaded = useAppSelector(selectInvitorLoaded);
  const childParticipants = useAppSelector(selectChildParticipants);
  const childIds = useMemo(
    () => childParticipants.map((participant) => participant.id),
    [childParticipants],
  );
  const childIdsKey = childIds.join(',');
  // Renamed from `cacheVersion` (which shadowed the module-scoped
  // `let cacheVersion = 0` at the top of the file). Semantically
  // it's the store snapshot version the hook is subscribed to.
  const cacheVersionSnapshot = useSyncExternalStore(
    subscribeToChildAlertsUnreadCache,
    getChildAlertsUnreadCacheVersion,
    getChildAlertsUnreadCacheVersion,
  );

  const [unreadByChildId, setUnreadByChildId] = useState<ChildAlertsUnreadMap>(
    {},
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Early exits are all "nothing to clean up" — make that explicit
    // with `return undefined` so ESLint's consistent-return rule sees
    // every branch return the same shape as the cancellation path
    // below.
    if (!enabled) {
      setUnreadByChildId({});
      setLoaded(true);
      return undefined;
    }

    if (!invitorLoaded) {
      dispatch(fetchInvitor()).catch(logErrors);
      setLoaded(false);
      return undefined;
    }

    if (childIds.length === 0) {
      setUnreadByChildId({});
      setLoaded(true);
      return undefined;
    }

    const allCached = childIds.every((childId) => unreadCache.has(childId));
    if (allCached) {
      setUnreadByChildId(
        Object.fromEntries(
          childIds.map((childId) => [childId, unreadCache.get(childId) ?? 0]),
        ),
      );
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;
    setLoaded(false);

    fetchChildAlertsUnreadCounts(dispatch, token ?? undefined, childIds)
      .then((result) => {
        if (!cancelled) {
          setUnreadByChildId(result);
          setLoaded(true);
        }
      })
      .catch((err) => {
        logErrors(err);
        if (!cancelled) {
          setUnreadByChildId({});
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    invitorLoaded,
    childIdsKey,
    dispatch,
    token,
    childIds,
    cacheVersionSnapshot,
  ]);

  const hasAnyUnread = useMemo(
    () => Object.values(unreadByChildId).some((count) => count > 0),
    [unreadByChildId],
  );

  return { unreadByChildId, hasAnyUnread, loaded };
}

export default useChildAlertsUnreadCounts;
